import { useCallback, useEffect, useRef, useState } from 'react';
import { CloseOutlined, LoadingOutlined, SearchOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { ProCard } from '@ant-design/pro-components';
import { App, Badge, Button, Empty, Flex, Input, Popconfirm, Select, Space, Tag, Typography } from 'antd';
import { useIntl } from 'react-intl';

import Permission from '@/components/permission';
import { contentWhitelistList, type ContentWhitelistItem } from '@/services/content-filter';
import { dramaList, type DramaItem } from '@/services/dramas';

const CREATE_PERMISSION = 'Client-Config_Content-Filter_Create';
const UPDATE_PERMISSION = 'Client-Config_Content-Filter_Update';
const DELETE_PERMISSION = 'Client-Config_Content-Filter_Delete';

const LIST_PAGE_SIZE = 999;
const SEARCH_DEBOUNCE_MS = 300;
const ID_PATTERN = /^\d+$/;

export type WhitelistSectionProps = {
    whitelist: ContentWhitelistItem[];
    bannerCount: number;
    bannerLimit: number;
    enabledVersionCount: number;
    adding: boolean;
    onAdd: (targetIds: string[]) => Promise<boolean>;
    onToggleBanner: (item: ContentWhitelistItem, banner: boolean) => void;
    onRemove: (item: ContentWhitelistItem) => void;
};

const WhitelistSection: React.FC<WhitelistSectionProps> = ({
    whitelist,
    bannerCount,
    bannerLimit,
    enabledVersionCount,
    adding,
    onAdd,
    onToggleBanner,
    onRemove,
}) => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();

    const [picked, setPicked] = useState<string[]>([]);
    const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
    const [optionSearching, setOptionSearching] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [results, setResults] = useState<ContentWhitelistItem[] | null>(null);
    const [listSearching, setListSearching] = useState(false);

    const addTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const addSeqRef = useRef(0);
    const listTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const listSeqRef = useRef(0);

    useEffect(() => {
        return () => {
            if (addTimerRef.current) {
                clearTimeout(addTimerRef.current);
            }
            if (listTimerRef.current) {
                clearTimeout(listTimerRef.current);
            }
        };
    }, []);

    /** 添加下拉远程搜索：短剧按标题模糊，纯数字按 ID */
    const handleContentSearch = useCallback(
        (value: string) => {
            if (addTimerRef.current) {
                clearTimeout(addTimerRef.current);
            }

            const trimmed = value.trim();

            if (!trimmed) {
                addSeqRef.current++;
                setOptions([]);
                setOptionSearching(false);
                return;
            }

            addTimerRef.current = setTimeout(async () => {
                const seq = ++addSeqRef.current;
                setOptionSearching(true);

                const keywordParams = ID_PATTERN.test(trimmed) ? { id: trimmed } : { title_fuzzy: trimmed };

                try {
                    const response = await dramaList({ page: 1, size: 20, drama_status: 'ONLINE', ...keywordParams });
                    const { data } = response;

                    if (seq !== addSeqRef.current) {
                        return;
                    }

                    if (data.code !== 200) {
                        setOptions([]);
                        return;
                    }

                    const list = (data.data ?? []) as DramaItem[];
                    setOptions(
                        list.map((item) => ({
                            label: `#${item.id} ${item.title || ''}`.trim(),
                            value: String(item.id),
                        })),
                    );
                } catch (error: any) {
                    if (seq === addSeqRef.current) {
                        message.error(
                            error?.response?.data?.msg ||
                                error?.message ||
                                formatMessage({ id: 'contentFilter.message.loadFailed' }),
                        );
                        setOptions([]);
                    }
                } finally {
                    if (seq === addSeqRef.current) {
                        setOptionSearching(false);
                    }
                }
            }, SEARCH_DEBOUNCE_MS);
        },
        [formatMessage, message],
    );

    /** 白名单列表远程搜索（服务端：短剧按标题模糊，纯数字 ID 精确） */
    const handleListSearch = useCallback(
        (value: string) => {
            if (listTimerRef.current) {
                clearTimeout(listTimerRef.current);
            }

            const trimmed = value.trim();
            setKeyword(value);

            if (!trimmed) {
                listSeqRef.current++;
                setResults(null);
                setListSearching(false);
                return;
            }

            listTimerRef.current = setTimeout(async () => {
                const seq = ++listSeqRef.current;
                setListSearching(true);

                try {
                    const response = await contentWhitelistList({
                        page: 1,
                        size: LIST_PAGE_SIZE,
                        target_type: 'drama',
                        keyword: trimmed,
                    });
                    const { data } = response;

                    if (seq !== listSeqRef.current) {
                        return;
                    }

                    if (data.code !== 200) {
                        message.error(data.msg || formatMessage({ id: 'contentFilter.message.loadFailed' }));
                        setResults(null);
                        return;
                    }

                    setResults(data.data ?? []);
                } catch (error: any) {
                    if (seq === listSeqRef.current) {
                        message.error(
                            error?.response?.data?.msg ||
                                error?.message ||
                                formatMessage({ id: 'contentFilter.message.loadFailed' }),
                        );
                        setResults(null);
                    }
                } finally {
                    if (seq === listSeqRef.current) {
                        setListSearching(false);
                    }
                }
            }, SEARCH_DEBOUNCE_MS);
        },
        [formatMessage, message],
    );

    const handleSubmitAdd = useCallback(async () => {
        if (picked.length === 0) {
            message.warning(formatMessage({ id: 'contentFilter.message.noIdsWarning' }));
            return;
        }

        const ok = await onAdd(picked);

        if (ok) {
            setPicked([]);
        }
    }, [formatMessage, message, onAdd, picked]);

    const displayed = results ?? whitelist;

    const renderRow = (item: ContentWhitelistItem) => (
        <Flex
            key={item.id}
            align="center"
            justify="space-between"
            gap={12}
            style={{ padding: '8px 4px', borderBottom: '1px solid #f0f0f0' }}
        >
            <Space size={10} style={{ overflow: 'hidden' }}>
                <Typography.Text code style={{ fontSize: 13 }}>
                    #{item.target_id}
                </Typography.Text>
                <Typography.Text type="secondary" ellipsis style={{ maxWidth: 360 }}>
                    {item.title || '-'}
                </Typography.Text>
            </Space>
            <Space size={4}>
                <Permission permission={UPDATE_PERMISSION}>
                    <Button
                        type="text"
                        size="small"
                        title={formatMessage({
                            id: item.banner
                                ? 'contentFilter.drama.removeBanner'
                                : 'contentFilter.drama.setBanner',
                        })}
                        icon={
                            item.banner ? (
                                <StarFilled style={{ color: '#faad14' }} />
                            ) : (
                                <StarOutlined
                                    style={{
                                        color: bannerCount >= bannerLimit ? '#d9d9d9' : undefined,
                                    }}
                                />
                            )
                        }
                        disabled={!item.banner && bannerCount >= bannerLimit}
                        onClick={() => onToggleBanner(item, !item.banner)}
                    />
                </Permission>
                <Permission permission={DELETE_PERMISSION}>
                    <Popconfirm
                        title={formatMessage({ id: 'contentFilter.removeConfirm.title' })}
                        description={
                            <div style={{ maxWidth: 320 }}>
                                <div>
                                    #{item.target_id} {item.title || ''}
                                </div>
                                <div style={{ marginTop: 4 }}>
                                    {formatMessage(
                                        { id: 'contentFilter.removeConfirm.impact' },
                                        { count: enabledVersionCount },
                                    )}
                                </div>
                                {item.banner && (
                                    <div style={{ marginTop: 2 }}>
                                        {formatMessage({ id: 'contentFilter.removeConfirm.bannerAlso' })}
                                    </div>
                                )}
                            </div>
                        }
                        okText={formatMessage({ id: 'contentFilter.removeConfirm.confirm' })}
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onRemove(item)}
                    >
                        <Button type="text" size="small" danger icon={<CloseOutlined />} />
                    </Popconfirm>
                </Permission>
            </Space>
        </Flex>
    );

    return (
        <ProCard
            title={
                <Space size={8}>
                    <span>{formatMessage({ id: 'contentFilter.drama.title' })}</span>
                    <Tag color="gold">
                        {formatMessage({ id: 'contentFilter.drama.bannerBadge' }, { count: bannerCount })}
                    </Tag>
                    <Badge count={whitelist.length} color="blue" showZero style={{ marginLeft: 4 }} />
                </Space>
            }
            headerBordered
        >
            <Flex gap={12} wrap style={{ marginBottom: 8 }}>
                <Select
                    mode="multiple"
                    filterOption={false}
                    tokenSeparators={[',', '，', ';', '；', ' ']}
                    value={picked}
                    onChange={setPicked}
                    onSearch={handleContentSearch}
                    options={options}
                    loading={optionSearching}
                    placeholder={formatMessage({ id: 'contentFilter.drama.addSearchPlaceholder' })}
                    style={{ flex: 1, minWidth: 320 }}
                />
                <Permission permission={CREATE_PERMISSION}>
                    <Button type="primary" loading={adding} onClick={handleSubmitAdd}>
                        {formatMessage({ id: 'contentFilter.drama.add' })}
                    </Button>
                </Permission>
            </Flex>
            <Input
                allowClear
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                suffix={listSearching ? <LoadingOutlined spin /> : null}
                value={keyword}
                onChange={(event) => handleListSearch(event.target.value)}
                placeholder={formatMessage({ id: 'contentFilter.drama.searchPlaceholder' })}
                style={{ width: 320, marginBottom: 8 }}
            />
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {displayed.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={formatMessage({ id: 'contentFilter.drama.empty' })}
                        style={{ padding: '24px 0' }}
                    />
                ) : (
                    displayed.map((item) => renderRow(item))
                )}
            </div>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                {formatMessage({ id: 'contentFilter.drama.bannerHint' })}
            </Typography.Paragraph>
        </ProCard>
    );
};

export default WhitelistSection;
